import placeholder from '../assets/img/demo/demo.png';
import React from 'react';

// Rich demo product card styled with Tailwind, driven by admin settings.
// Accepts a `settings` prop (object) so parent can control live updates.
const DemoProductCard = ({ settings = {} }) => {
  const general = (settings && settings.tpwvs_general) || {};
  const shop = (settings && settings.tpwvs_shop) || {};
  const style = (settings && settings.tpwvs_style) || {};

  const swatchSize = parseInt(general.swatch_size || style.swatch_size || 26, 10);
  const swatchStyle = general.swatch_style || 'square';
  const enableSwatches = shop.enable_swatches !== undefined ? !!shop.enable_swatches : true;
  const showLabel = !!shop.swatch_label;
  const swatchAlignment = shop.swatch_alignments || 'left';
  const swatchPosition = shop.swatch_position || 'after_title';

  const alignmentMap = {
    left: 'items-start',
    center: 'items-center',
    right: 'items-end',
  };
  const alignmentClass = alignmentMap[swatchAlignment] || alignmentMap.left;

  const enableTooltip = general.tooltip !== undefined ? !!general.tooltip : true;
  const tooltipBg = style.tooltip_background || general.tooltip_background || '#000';
  const tooltipColor = style.tooltip_font_color || general.tooltip_font_color || '#fff';
  const tooltipPosition = general.tooltip_position || style.tooltip_position || 'top';
  
  // Demo product data
  const product = {
    title: 'Minimal Cotton Tee',
    price: 29.99,
    image: (settings && settings.demo_image) || placeholder,
    attributes: [
      { key: 'pa_color', name: 'Color', type: 'color', values: [ { id: 'red', label: 'Red', value: '#ef4444' }, { id: 'blue', label: 'Blue', value: '#3b82f6' }, { id: 'green', label: 'Green', value: '#10b981' } ] },
      { key: 'pa_size', name: 'Size', type: 'select', values: [ { id: 's', label: 'S' }, { id: 'm', label: 'M' }, { id: 'l', label: 'L' } ] },
    ],
  };

  const [selected, setSelected] = React.useState({ color: product.attributes[0].values[0].id, size: product.attributes[1].values[0].id });
  const [adding, setAdding] = React.useState(false);

  const swatchClass = swatchStyle === 'tp-square' ? 'rounded-sm' : 'rounded-full';

  // refs for swatch container and individual color buttons to compute tooltip position
  const swatchesContainerRef = React.useRef(null);
  const swatchButtonRefs = React.useRef({});

  const [tooltipStyle, setTooltipStyle] = React.useState({ display: 'none' });

  React.useLayoutEffect(() => {
    // function to compute and set tooltip position relative to selected swatch
    const compute = () => {
      const colorAttr = product.attributes.find((a) => a.type === 'color');
      if (!colorAttr || !enableTooltip) {
        setTooltipStyle({ display: 'none' });
        return;
      }
      const selectedColorId = (selected && selected.color) || (colorAttr.values[0] && colorAttr.values[0].id);
      const btn = swatchButtonRefs.current[selectedColorId];
      const container = swatchesContainerRef.current;
      if (!btn || !container) {
        setTooltipStyle({ display: 'none' });
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offset = 8; // gap between swatch and tooltip

      let style = { position: 'absolute', display: 'block' };

      if (tooltipPosition === 'left') {
        style.left = `${btnRect.left - containerRect.left - offset}px`;
        style.top = `${btnRect.top - containerRect.top + btnRect.height / 2}px`;
        style.transform = 'translate(-100%, -50%)';
      } else if (tooltipPosition === 'right') {
        style.left = `${btnRect.right - containerRect.left + offset}px`;
        style.top = `${btnRect.top - containerRect.top + btnRect.height / 2}px`;
        style.transform = 'translate(0%, -50%)';
      } else if (tooltipPosition === 'bottom') {
        style.left = `${btnRect.left - containerRect.left + btnRect.width / 2}px`;
        style.top = `${btnRect.bottom - containerRect.top + offset}px`;
        style.transform = 'translate(-50%, 0%)';
      } else {
        // top (default)
        style.left = `${btnRect.left - containerRect.left + btnRect.width / 2}px`;
        style.top = `${btnRect.top - containerRect.top - offset}px`;
        style.transform = 'translate(-50%, -100%)';
      }

      setTooltipStyle(style);
    };

    // compute initially
    compute();

    // recompute on resize in case layout changes
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [selected.color, tooltipPosition, tooltipBg, tooltipColor, enableTooltip, swatchSize, showLabel, swatchAlignment, swatchPosition, enableSwatches]);

  const onSelect = (attrKey, val) => {
    setSelected((s) => ({ ...s, [attrKey]: val }));
  };

  const addToCart = () => {
    setAdding(true);
    setTimeout(() => setAdding(false), 800);
  };

  const renderSwatches = () => {
    // determine color and size attributes (fall back to defaults if needed)
    const colorAttr = product.attributes && product.attributes.find((a) => a.type === 'color' || (a.name && a.name.toLowerCase().includes('color')) || (a.key && a.key.toLowerCase().includes('color')));
    const sizeAttr = product.attributes && product.attributes.find((a) => (a.name && a.name.toLowerCase().includes('size')) || (a.key && a.key.toLowerCase().includes('size')) || a.type === 'select');

    const defaultColors = [
      { id: 'red', label: 'Red', value: '#ef4444' },
      { id: 'blue', label: 'Blue', value: '#3b82f6' },
      { id: 'green', label: 'Green', value: '#10b981' },
      { id: 'gray', label: 'Gray', value: '#6b7280' },
    ];

    const colorValues = colorAttr && colorAttr.values && colorAttr.values.length ? colorAttr.values : defaultColors;
    const sizeValues = sizeAttr && sizeAttr.values && sizeAttr.values.length ? sizeAttr.values : null;

    const tooltipInlineStyle = { background: tooltipBg, color: tooltipColor, position: 'absolute', padding: '6px 10px', borderRadius: 6, zIndex: 50, ...tooltipStyle };

    const arrowStyle = (() => {
      // small caret under/over/left/right of tooltip
      const base = { position: 'absolute', width: 0, height: 0 };
      if (tooltipPosition === 'left') return { ...base, right: -6, top: '50%', transform: 'translateY(-50%)', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: `6px solid ${tooltipBg}` };
      if (tooltipPosition === 'right') return { ...base, left: -6, top: '50%', transform: 'translateY(-50%)', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: `6px solid ${tooltipBg}` };
      if (tooltipPosition === 'bottom') return { ...base, top: -6, left: '50%', transform: 'translateX(-50%)', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `6px solid ${tooltipBg}` };
      // top default
      return { ...base, bottom: -6, left: '50%', transform: 'translateX(-50%)', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `6px solid ${tooltipBg}` };
    })();

    return (
      <div ref={swatchesContainerRef} className={`relative flex flex-col ${alignmentClass} gap-4`}>
        {/* Color block */}
        <div className={`mb-3 w-full flex flex-col ${alignmentClass}`}>
          {showLabel && <div className="text-xl text-gray-500 mb-2">Color</div>}
          <div className={`flex justify-center gap-2`}>
            {colorValues.map((v) => {
              const isImage = colorAttr && colorAttr.type === 'image';
              const key = `color-${v.id}`;
              const isSelected = selected && selected.color === v.id;
              return (
                <button
                  key={key}
                  ref={(el) => { if (el) swatchButtonRefs.current[v.id] = el; }}
                  onClick={() => onSelect('color', v.id)}
                  aria-label={v.label}
                  className={`${swatchClass} flex items-center justify-center border border-gray-200 focus:outline-none`}
                  style={{ width: swatchSize, height: swatchSize, minWidth: swatchSize, minHeight: swatchSize }}
                >
                  {isImage ? (
                    <img src={v.value} alt={v.label} className={`w-full h-full object-cover ${swatchClass}`} />
                  ) : (
                    <span 
                    className={`w-full h-full object-cover ${swatchClass}`}
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      height: '100%', 
                      background: v.value 
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Size block (static) */}
        {sizeValues && (
          <div className={`mb-3 w-full flex flex-col ${alignmentClass}`}>
            {showLabel && <div className="text-xl text-gray-500 mb-2">Size</div>}
            <div className={`flex justify-center gap-2`}>
              {sizeValues.map((v) => (
                <button
                  key={`size-${v.id}`}
                  onClick={() => onSelect('size', v.id)}
                  className={`px-3 py-1 border rounded ${swatchClass} ${selected && selected.size === v.id ? 'bg-gray-200' : 'bg-white'}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tooltip (single) */}
        {enableTooltip && (
          <div style={tooltipInlineStyle} aria-hidden="true">
            <span style={{ position: 'relative', zIndex: 2 }}>
              {(() => {
                const cv = colorValues.find((c) => c.id === (selected && selected.color)) || colorValues[0];
                return cv ? cv.label : '';
              })()}
            </span>
            <span style={arrowStyle} aria-hidden="true" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tpv-preview-page bg-gray rounded-3xl pb-[62px] h-full">
      <div className="bg-white block max-w-md w-full mx-auto border border-default rounded-lg shadow-xs overflow-hidden">
        <a href="#" className="block">
          <img className="w-full object-cover h-56" src={product.image} alt="" />
        </a>
        <div className="p-6 text-center">
          <a href="#" className="block">
            <h5 className="mt-3 mb-4 text-2xl font-semibold tracking-tight text-heading">{product.title}</h5>
          </a>

          {/* Swatches after title */}
          {enableSwatches && (swatchPosition === 'woocommerce_after_shop_loop_item_title' || swatchPosition === 'after_title') && (
            <div className="mb-4">{renderSwatches()}</div>
          )}

          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="text-xl font-bold text-gray-900">${product.price.toFixed(2)}</div>
            <div className="text-sm text-gray-500">In stock</div>
          </div>

          {/* Swatches after price */}
          {enableSwatches && (swatchPosition === 'woocommerce_after_shop_loop_item' || swatchPosition === 'after_price') && (
            <div className="mb-4">{renderSwatches()}</div>
          )}

          <a href="#" onClick={(e)=>{ e.preventDefault(); addToCart(); }} className="inline-flex items-center text-white bg-indigo-600 border border-transparent hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 shadow-xs font-medium rounded-md text-sm px-4 py-2.5 focus:outline-none">
            {adding ? 'Adding…' : 'Add to cart'}
            <svg className="w-4 h-4 ml-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 12H5m14 0-4 4m4-4-4-4"/></svg>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoProductCard;
